import * as React from 'react'

import Box from '@mui/material/Box'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'

import { BillTo } from '@user/store/entityLocations'
import { useToggleBoolean } from '@user/utils/useToggleBoolean'
import { BillToSearchSelectContents } from '@user/components/Location/BillToSelectScroll/BillToSelectCard/BillToSearchCard/BillToSearchSelectContents'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'

type Props = {
  location: BillTo
  onClear: () => void
}

export function BillToSelectChip(props: Props) {
  const { location, onClear } = props
  const [open, toggleOpen, setOpen] = useToggleBoolean(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const tidalThemeActive = useTidalThemeActive()

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClear()
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggleOpen()
  }

  const handleClose = (e: MouseEvent | TouchEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(e.target as HTMLElement)
    ) {
      return
    }
    setOpen(false)
  }

  const displayName =
    `${location.firstName ?? ''} ${location.lastName ?? ''}`.trim() ||
    location.company ||
    'Selected Location'

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        ref={anchorRef}
        position="relative"
        sx={{
          width: '100%',
          height: tidalThemeActive ? '64px' : '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          borderRadius: '4px',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          px: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: tidalThemeActive ? '14px' : '12px',
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)',
            position: 'absolute',
            top: '-5px',
            left: '11px',
            backgroundColor: 'white',
            lineHeight: '0.5',
            px: '4px',
          }}
        >
          Bill-To Location
        </Typography>

        {/* Chip */}
        <Box
          onClick={handleClick}
          data-test-id="billToSelectedChip"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: open ? '#D4E3F3' : 'rgba(9, 27, 42, 0.08)',
            borderRadius: '16px',
            padding: '4px 8px 4px 4px',
            cursor: 'pointer',
            height: '32px',
            maxWidth: 'fit-content',
            border: `1px solid ${open ? '#699DD7' : 'rgba(9, 27, 42, 0.56)'}`,
            userSelect: 'none',
          }}
        >
          <Box display="flex" alignItems="center" overflow="hidden">
            <ArrowDropDown
              sx={{
                width: 20,
                height: 20,
                color: '#6c6d6d',
                mr: '6px',
              }}
            />

            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                mr: '6px',
              }}
            >
              {displayName}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={handleClear}
            data-test-id="clearSelectedBillTo"
          >
            <CloseIcon
              sx={{
                fontSize: 20,
                backgroundColor: 'rgba(0,0,0,0.56)',
                color: 'white',
                borderRadius: '50%',
                padding: '2px',
              }}
            />
          </IconButton>
        </Box>

        {/* Dropdown */}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{
            zIndex: 1300,
            width: '100%',
          }}
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: 'viewport',
                padding: 8,
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                tether: false,
                rootBoundary: 'viewport',
                padding: 8,
              },
            },
            {
              name: 'width',
              enabled: true,
              phase: 'beforeWrite',
              requires: ['computeStyles'],
              fn: ({ state }) => {
                state.styles.popper.width = `${state.rects.reference.width}px`
              },
            },
          ]}
        >
          <Paper
            sx={{
              mt: 1,
              mb: 1,
              maxHeight: 400,
              overflowY: 'auto',
              boxShadow: 4,
              p: 2,
            }}
          >
            <BillToSearchSelectContents location={location} viewOnly />
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
